/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface CreativeSetModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface CreativeSetCopyOptions {
  name: string;
  copyCreatives?: boolean;
  userState?: string;
}

@Injectable({providedIn: 'root'})
export class CreativeSetApiService extends SignalAdminResourceApiBase<CreativeSetModel> {
  constructor(
    client: AdnApiClientService,
    @Inject(LocalNetworkProfile) private readonly networkProfile: any
  ) {
    super(client, 'creativesets');
  }

  override saveMany(models: Partial<CreativeSetModel>[]): Observable<void> {
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri('creativesets'),
      params: {
        ignoreProduct: this.networkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting']
      },
      body: models
    });
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<CreativeSetModel>> {
    return this.client.request<SignalAdminResourcePage<CreativeSetModel>>({
      method: 'GET',
      url: this.resourceUri('creativesets'),
      params
    });
  }

  copy(sourceId: string, newId: string, options: CreativeSetCopyOptions): Observable<void> {
    const body = {
      id: sourceId,
      creativeSetId: newId || '',
      name: options.name,
      copyCreatives: options.copyCreatives ?? true,
      userState: options.userState || ''
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['creativesets', sourceId, 'copy']),
      params: {
        creativeSetId: body.creativeSetId,
        name: body.name,
        copyCreatives: body.copyCreatives,
        userState: body.userState
      },
      body
    });
  }
}
